// What this browser remembers about the wishlist.
//
// There are no accounts, so the Saved state on a button and the address
// prefilled into the modal both come from here. The database is the truth; this
// is only what lets the UI avoid asking the same person the same question twice.
//
// localStorage rather than sessionStorage, because a wishlist that forgets
// itself when the tab closes is not a wishlist. Every access is wrapped: reading
// storage throws outright in some privacy modes, and losing a "Saved" pill is
// not worth a crashed render.

const EMAIL_KEY = "plample:wishlist:email";
const SAVED_KEY = "plample:wishlist:saved";

/** Fires on this tab; the native `storage` event only reaches other tabs. */
export const WISHLIST_EVENT = "plample:wishlist:change";

function readSaved(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SAVED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Config ids this browser has saved. Country-scoped: the same laptop watched
 *  for GB and US is two subscriptions, and the button should reflect that. */
export function savedKey(configId: string, country: string): string {
  return `${country}:${configId}`;
}

export function isSaved(configId: string, country: string): boolean {
  return readSaved().includes(savedKey(configId, country));
}

export function markSaved(configId: string, country: string): void {
  try {
    const key = savedKey(configId, country);
    const next = readSaved();
    if (!next.includes(key)) next.push(key);
    window.localStorage.setItem(SAVED_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
  } catch {
    /* private mode — the button just won't remember, which is survivable */
  }
}

/** The address last used, so saving a second laptop is one click. */
export function readEmail(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

export function writeEmail(email: string): void {
  try {
    window.localStorage.setItem(EMAIL_KEY, email);
  } catch {
    /* as above */
  }
}
